<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900">
    <v-card>
      <v-card-title>
        Where is this address?
        <v-spacer/>
        <v-btn
          color="success"
          :disabled="!latLng || looking"
          :loading="saving"
          data-cy="confirm-coordinates"
          @click="confirm"
        >
          Confirm
        </v-btn>
        <v-btn icon class="ml-2" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>{{ address }}</v-card-subtitle>
      <v-card-text>
        <v-progress-linear v-if="looking" indeterminate class="mb-2"/>
        <v-alert v-if="lookupError" type="warning" outlined dense>
          {{ lookupError }}. In Google Maps, right-click the house and click the numbers to copy them,
          then paste them below.
        </v-alert>
        <div v-if="google" class="mb-2" data-cy="google-match">
          <b>Google matched:</b> {{ google.formattedAddress || '?' }}
          &middot; {{ precisionLabel(google.locationType) }}
          <div class="text-caption">
            Check the pin is on the right house. If it is not, paste the right coordinates below.
          </div>
          <v-alert v-for="warning of google.warnings" :key="warning" type="warning" outlined dense class="mt-1 mb-0">
            {{ warning }}
          </v-alert>
        </div>
        <div class="d-flex align-center mb-2">
          <v-text-field
            v-model="input"
            dense outlined hide-details
            label="latitude, longitude"
            placeholder="eg 49.2827, -123.1207"
            style="max-width: 300px"
            data-cy="map-coordinates-input"
          />
          <a v-if="latLng" :href="mapsUrl(latLng)" target="_blank" rel="noopener" class="ml-3 text-caption">
            open in Google Maps
          </a>
          <span v-if="moved" class="ml-3 text-caption warning--text">changed from Google's pin</span>
        </div>
        <div v-if="inputError" class="error--text text-caption mb-2">{{ inputError }}</div>
        <iframe
          v-if="latLng"
          :src="embedUrl(latLng)"
          title="Map of the coordinates"
          style="width: 100%; height: 420px; border: 0"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        />
        <div v-if="saveError" class="error--text mt-2">{{ saveError }}</div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * For a stop Onfleet could not geocode: looks the address up with Google from the browser
 * (utils/google-geocode.js, nothing saved), shows the pin on a map, and saves it on the
 * stop's customers only when someone confirms it (POST v2/onfleet/coordinates).  The
 * coordinates can be corrected before confirming; a changed pin is saved as typed in.
 */
import api from '@/api';
import {mapsUrl, parseLatLng} from '@/utils/address-coordinates';
import {geocodeAddress} from '@/utils/google-geocode';

const PRECISION = {
  ROOFTOP: 'exact building',
  RANGE_INTERPOLATED: 'estimated along the street - check the house',
  GEOMETRIC_CENTER: 'centre of the street or area - check the house',
};

export default {
  name: 'CoordinateConfirmDialog',
  props: {
    value: {type: Boolean, default: false},
    /**
     * the stop: address (text), deliveryAddress (the customer's address record, which may
     * hold the place id from signup), uids and coordinateDate, as ShipmentStopCard has it
     */
    stop: {type: Object, required: true},
  },
  data() {
    return {
      looking: false,
      lookupError: '',
      google: null,
      input: '',
      saving: false,
      saveError: '',
    };
  },
  computed: {
    address() {
      return this.stop.address;
    },
    parsed() {
      try {
        return {latLng: parseLatLng(this.input)};
      } catch (e) {
        return {error: this.input ? e.message : ''};
      }
    },
    latLng() {
      return this.parsed.latLng || null;
    },
    inputError() {
      return this.parsed.error || '';
    },
    moved() {
      return !!(this.google && this.latLng
        && (this.latLng.lat !== this.google.lat || this.latLng.lng !== this.google.lng));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          this.lookUp();
        }
      },
    },
  },
  methods: {
    mapsUrl,
    embedUrl({lat, lng}) {
      return `https://maps.google.com/maps?q=${lat},${lng}&z=18&output=embed`;
    },
    precisionLabel(locationType) {
      return PRECISION[locationType] || locationType || 'precision unknown';
    },
    async lookUp() {
      this.looking = true;
      this.lookupError = '';
      this.saveError = '';
      this.google = null;
      this.input = '';
      try {
        this.google = await geocodeAddress(this.stop.deliveryAddress || this.stop.address);
        this.input = `${this.google.lat}, ${this.google.lng}`;
      } catch (e) {
        this.lookupError = e.message;
      }
      this.looking = false;
    },
    async confirm() {
      this.saving = true;
      this.saveError = '';
      const {uids, coordinateDate: date} = this.stop;
      try {
        // google's lookup goes along so an unchanged pin is saved as google found it
        const google = this.google && {
          lat: this.google.lat, lng: this.google.lng, source: this.google.source,
          locationType: this.google.locationType, placeId: this.google.placeId,
          formattedAddress: this.google.formattedAddress,
        };
        const {data} = await api.post('v2/onfleet/coordinates', {
          uids, date, coordinates: this.latLng, google: google || undefined
        });
        if (data.missed && data.missed.length) {
          this.saveError = `Could not find the address record for ${data.missed.join(', ')}`;
        }
        this.$emit('saved', {uids, date, coordinates: data.coordinates});
        if (!this.saveError) {
          this.$emit('input', false);
        }
      } catch (e) {
        const data = e.response && e.response.data;
        this.saveError = (data && data.error) || e.message;
      }
      this.saving = false;
    },
  },
}
</script>
